import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { KpiCard, Panel, RiskBadge, StatusChip } from "@/lovable-demo/components/bits";
import { cn } from "@/lib/utils";
import { recordsForProgram } from "@/lovable-demo/lib/warranty/aggregate";
import { fmtCompactCurrency, fmtDate, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import { SCOPE_LABEL, assessAll, type SafetyScope } from "@/lovable-demo/lib/warranty/safety";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/safety")({
  head: () => ({
    meta: [
      { title: "Safety & NHTSA Compliance — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Isolate potential safety defects and FMVSS noncompliance across governed Problem Records, with 49 CFR 573 reporting clocks, Part 579 Early Warning Reporting and owner notification duties.",
      },
      { property: "og:title", content: "Safety & NHTSA Compliance — Broadview" },
      {
        property: "og:description",
        content:
          "Potential safety defect and FMVSS noncompliance screening with Part 573 five-working-day reporting clocks and Early Warning Reporting duties.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SafetyCompliance,
});

const scopeTone: Record<SafetyScope, string> = {
  "safety-defect-candidate": "border-destructive/40 bg-destructive/15 text-destructive",
  "compliance-candidate": "border-warning/40 bg-warning/12 text-warning",
  monitor: "border-info/40 bg-info/12 text-info",
  "out-of-scope": "border-border bg-elevated text-muted-foreground",
};

function SafetyCompliance() {
  const { data, claims, activeProgramId } = useWarranty();
  const [scopeFilter, setScopeFilter] = useState<SafetyScope | "all">("all");
  const [selected, setSelected] = useState<string | null>(null);

  const records = recordsForProgram(data, activeProgramId);
  const program = data.programs.find((p) => p.id === activeProgramId);
  const all = useMemo(() => assessAll(records, claims), [records, claims]);
  const inScope = all.filter((a) => a.scope !== "out-of-scope");
  const defects = all.filter((a) => a.scope === "safety-defect-candidate");
  const compliance = all.filter((a) => a.scope === "compliance-candidate");
  const breached = inScope.filter((a) => a.clockStatus === "breached");
  const unverified = inScope.filter((a) => !a.containmentVerified);
  const exposure = inScope.reduce((a, x) => a + x.record.financial.exposure, 0);

  const shown = scopeFilter === "all" ? all : all.filter((a) => a.scope === scopeFilter);
  const detail = all.find((a) => a.record.id === selected) ?? shown[0];

  return (
    <AppShell
      title="Safety & NHTSA Compliance"
      subtitle={
        program
          ? `${program.code} · ${program.name} · potential safety defect and FMVSS screening`
          : "All programs · potential safety defect and FMVSS screening"
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Safety defect candidates"
          value={String(defects.length)}
          delta={`${compliance.length} compliance candidates`}
          hint="Screened against FMVSS by affected system"
          tone="bad"
        />
        <KpiCard
          label="Part 573 clock at risk"
          value={String(breached.length)}
          delta={`${inScope.filter((a) => a.clockStatus === "due-soon").length} due within 2 days`}
          hint="5 working days from first knowledge (49 CFR 573.6)"
          tone={breached.length ? "bad" : "good"}
        />
        <KpiCard
          label="Containment unverified"
          value={String(unverified.length)}
          hint="Interim customer protection not proven on every scope"
          tone="warn"
        />
        <KpiCard
          label="Exposure in safety scope"
          value={fmtCompactCurrency(exposure)}
          delta={`${fmtNumber(inScope.reduce((a, x) => a + x.record.claims, 0))} claims`}
          hint="Records inside the safety / compliance screen"
          tone="bad"
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {(
          [
            "all",
            "safety-defect-candidate",
            "compliance-candidate",
            "monitor",
            "out-of-scope",
          ] as const
        ).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setScopeFilter(key)}
            className={cn(
              "rounded border px-2.5 py-1 text-xs transition-colors",
              scopeFilter === key
                ? "border-primary/50 bg-primary/12 text-primary"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {key === "all" ? "All records" : SCOPE_LABEL[key]}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Panel
          title="Safety screen"
          subtitle="Records ranked by safety severity, affected system and reporting clock"
          className="xl:col-span-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Record</th>
                  <th className="px-2 py-2 text-left font-medium">System / FMVSS</th>
                  <th className="px-2 py-2 text-left font-medium">Scope / 573 report</th>
                  <th className="px-2 py-2 text-right font-medium">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {shown.map((a) => (
                  <tr
                    key={a.record.id}
                    onClick={() => setSelected(a.record.id)}
                    className={cn(
                      "cursor-pointer transition-colors hover:bg-elevated/60",
                      detail?.record.id === a.record.id && "bg-elevated/70",
                    )}
                  >
                    <td className="px-2 py-2.5">
                      <span className="num block text-xs text-primary">
                        {a.record.code} · {fmtCompactCurrency(a.record.financial.exposure)}
                      </span>
                      <span className="block max-w-[190px] truncate">{a.record.title}</span>
                      <span className="mt-1 flex flex-wrap gap-1.5">
                        <RiskBadge risk={a.record.risk} score={a.record.riskScore} />
                        <StatusChip status={a.record.status} />
                      </span>
                    </td>
                    <td className="px-2 py-2.5">
                      <span className="block max-w-[200px] truncate">{a.system.system}</span>
                      <span className="num block text-xs text-muted-foreground">
                        {a.system.fmvss}
                      </span>
                    </td>
                    <td className="px-2 py-2.5">
                      <span
                        className={cn(
                          "inline-flex rounded border px-2 py-0.5 text-[0.68rem] font-medium uppercase tracking-wider",
                          scopeTone[a.scope],
                        )}
                      >
                        {SCOPE_LABEL[a.scope]}
                      </span>
                      <span className="mt-1 block text-[0.7rem]">
                        {a.clockStatus === "not-triggered" ? (
                          <span className="text-muted-foreground">573 clock not triggered</span>
                        ) : a.clockStatus === "filed" ? (
                          <span className="text-success">
                            573 filed{" "}
                            <span className="num text-muted-foreground">
                              (due {fmtDate(a.part573Due)})
                            </span>
                          </span>
                        ) : (
                          <span
                            className={cn(
                              "num",
                              a.clockStatus === "breached"
                                ? "text-destructive"
                                : a.clockStatus === "due-soon"
                                  ? "text-warning"
                                  : "text-foreground",
                            )}
                          >
                            573 due {fmtDate(a.part573Due)} ·{" "}
                            {a.daysToDue < 0
                              ? `${Math.abs(a.daysToDue)}d past due`
                              : `${a.daysToDue}d left`}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="num px-2 py-2.5 text-right font-semibold">{a.safetyScore}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="space-y-4">
          {detail && (
            <Panel
              title="Screening rationale"
              subtitle={`${detail.record.code} · ${detail.system.system}`}
              actions={
                <Link
                  to="/demo/problem-record/$id"
                  params={{ id: detail.record.id }}
                  className="text-xs text-primary hover:underline"
                >
                  Open record
                </Link>
              }
            >
              <p className="text-sm text-foreground/90">{detail.record.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {detail.system.fmvss} — {detail.system.fmvssTitle}
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-muted-foreground">First knowledge</p>
                  <p className="num mt-0.5 font-semibold">{fmtDate(detail.firstKnowledge)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">EWR quarter</p>
                  <p className="num mt-0.5 font-semibold">{detail.ewrQuarter}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Claims screened</p>
                  <p className="num mt-0.5 font-semibold">{fmtNumber(detail.claimCount)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Hazard-language claims</p>
                  <p className="num mt-0.5 font-semibold">{fmtNumber(detail.hazardClaims)}</p>
                </div>
              </div>
              <ul className="mt-3 space-y-2 text-xs text-foreground/85">
                {detail.rationale.map((line) => (
                  <li key={line} className="flex gap-2">
                    <ShieldAlert className="mt-0.5 size-3.5 shrink-0 text-primary" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {detail && (
            <Panel title="Regulatory obligations" subtitle="49 CFR duties attached to this record">
              <ul className="space-y-2.5 text-xs">
                {detail.obligations.map((o) => (
                  <li key={o.ref} className="border-b border-border/60 pb-2 last:border-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="num font-semibold text-foreground">{o.ref}</span>
                      <span
                        className={cn(
                          "rounded border px-1.5 py-0.5 text-[0.62rem] font-medium uppercase tracking-wider",
                          o.state === "met"
                            ? "border-success/40 bg-success/12 text-success"
                            : o.state === "at-risk"
                              ? "border-destructive/40 bg-destructive/15 text-destructive"
                              : o.state === "open"
                                ? "border-warning/40 bg-warning/12 text-warning"
                                : "border-border bg-elevated text-muted-foreground",
                        )}
                      >
                        {o.state}
                      </span>
                    </div>
                    <p className="mt-0.5 text-foreground/85">{o.title}</p>
                    <p className="mt-0.5 text-muted-foreground">{o.requirement}</p>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      </div>

      <Panel className="mt-4" title="How this screen works">
        <p className="flex gap-2 text-xs leading-relaxed text-muted-foreground">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
          <span>
            Each governed Problem Record is matched to the vehicle system it affects and the
            applicable Federal Motor Vehicle Safety Standard, then scored on hazard language, field
            claim volume, governed risk rating and containment state. Records scoring in the safety
            band start a 49 CFR 573.6 five-working-day reporting clock from first knowledge, and all
            in-scope records carry Part 579 Early Warning Reporting duties. This is an engineering
            screen to isolate candidates for the safety committee — it does not make the
            manufacturer's defect decision or replace legal review.
          </span>
        </p>
      </Panel>
    </AppShell>
  );
}
